import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { renderInline } from "./blocks/renderInline";

// Tap-through slideshow over a chapter's media (order + captions come straight from the block editor).
export const Slideshow = ({ slides, start = 0, title = "", onClose, linkColor = "#1D4ED8" }) => {
  const [i, setI] = useState(start);
  const n = slides.length;
  const go = (d) => setI((x) => (x + d + n) % n);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [n, onClose]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!n) return null;
  const s = slides[i];
  let x0 = 0;
  return createPortal(
    <div className="fixed inset-0 z-[3000] bg-[#0A0A0F] text-white flex flex-col" role="dialog" aria-modal="true" data-testid="story-slideshow"
      onTouchStart={(e) => { x0 = e.touches[0].clientX; }} onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - x0; if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); }}>
      <div className="flex items-center justify-between px-4 py-3 text-xs sm:text-sm">
        <div className="font-bold uppercase tracking-[0.2em] text-white/70 truncate pr-4">{title}</div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="tabular-nums text-white/70" data-testid="story-slideshow-counter">{i + 1} / {n}</span>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-full bg-white/15 hover:bg-white/30 p-2" data-testid="story-slideshow-close"><X className="h-5 w-5" /></button>
        </div>
      </div>
      <div className="relative flex-1 min-h-0 flex items-center justify-center px-12 sm:px-20">
        <button type="button" onClick={() => go(-1)} aria-label="Previous" className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 hover:bg-white/30 p-2 sm:p-3" data-testid="story-slideshow-prev"><ChevronLeft className="h-6 w-6" /></button>
        {s.video ? <video key={s.video} src={s.video} controls autoPlay playsInline className="max-h-full max-w-full rounded-lg shadow-2xl bg-black" />
          : <img key={s.image} src={s.image} alt={s.caption || ""} className="max-h-full max-w-full object-contain rounded-lg shadow-2xl" data-testid="story-slideshow-image" />}
        <button type="button" onClick={() => go(1)} aria-label="Next" className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 hover:bg-white/30 p-2 sm:p-3" data-testid="story-slideshow-next"><ChevronRight className="h-6 w-6" /></button>
      </div>
      <div className="px-5 sm:px-20 py-4 text-center">
        {s.label && <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300">{s.label}</div>}
        {s.caption && <p className="mt-1 text-sm sm:text-base leading-relaxed text-white/90 max-w-3xl mx-auto" data-testid="story-slideshow-caption">{renderInline(s.caption, "#FCD34D")}</p>}
      </div>
    </div>,
    document.body
  );
};

export const SlideshowButton = ({ onClick, count, dark }) => (
  <button type="button" onClick={onClick} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${dark ? "border-white/30 text-white hover:bg-white/10" : "border-[#1A1A1A]/20 text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white"}`} data-testid="story-slideshow-open">
    <Play className="h-3.5 w-3.5" /> Slideshow · {count}
  </button>
);

export default Slideshow;
